import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { CaseSensitiveToggle } from "@/components/common/CaseSensitiveToggle";
import {
	SEARCH_FIELD_CLASS,
	SEARCH_FRAME_CLASS,
	SearchInput,
} from "@/components/common/SearchInput";
import { IconButton } from "@/components/common/IconButton";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import type { LabelMatch } from "./labelSearch";

interface SegmentLabelSearchBarProps {
	query: string;
	caseSensitive: boolean;
	replacement: string;
	matches: readonly LabelMatch[];
	activeMatchIndex: number;
	onQueryChange: (query: string) => void;
	onReplacementChange: (replacement: string) => void;
	onToggleCaseSensitive: () => void;
	onPrevious: () => void;
	onNext: () => void;
	onReplace: () => void;
	onReplaceAll: () => void;
	onClose: () => void;
}

export function SegmentLabelSearchBar({
	query,
	caseSensitive,
	replacement,
	matches,
	activeMatchIndex,
	onQueryChange,
	onReplacementChange,
	onToggleCaseSensitive,
	onPrevious,
	onNext,
	onReplace,
	onReplaceAll,
	onClose,
}: SegmentLabelSearchBarProps) {
	const inputRef = useRef<HTMLInputElement>(null);
	const [replacementOpen, setReplacementOpen] = useState(false);

	useEffect(() => {
		inputRef.current?.focus();
		inputRef.current?.select();
	}, []);

	const handleQueryKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
		if (event.nativeEvent.isComposing) return;
		if (event.key === "Escape") {
			event.preventDefault();
			onClose();
		} else if (event.key === "Enter") {
			event.preventDefault();
			if (event.shiftKey) onPrevious();
			else onNext();
		}
	};

	const handleReplacementKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
		if (event.nativeEvent.isComposing) return;
		if (event.key === "Escape") {
			event.preventDefault();
			onClose();
		}
	};

	const countLabel = matches.length === 0 ? "0/0" : `${activeMatchIndex + 1}/${matches.length}`;

	return (
		<div
			className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-1 gap-y-0.5 px-3 pb-1"
			data-testid="segment-label-search-bar"
		>
			<div className="flex min-w-0 items-center gap-1">
				<IconButton
					icon={replacementOpen ? "chevronD" : "chevronR"}
					iconSize={13}
					size={22}
					onClick={() => setReplacementOpen((open) => !open)}
					data-testid="segment-label-replacement-toggle"
				/>
				<SearchInput
					ref={inputRef}
					variant="framed"
					value={query}
					onChange={onQueryChange}
					onKeyDown={handleQueryKeyDown}
					placeholder="ラベルを検索"
					testId="segment-label-search-input"
					trailing={<CaseSensitiveToggle active={caseSensitive} onToggle={onToggleCaseSensitive} />}
				/>
			</div>
			<div className="flex items-center justify-end gap-1">
				<span className="shrink-0 px-1 font-mono text-section text-fg-3">{countLabel}</span>
				<IconButton
					icon="chevronU"
					iconSize={13}
					size={22}
					disabled={matches.length === 0}
					onClick={onPrevious}
					title="前の一致"
					data-testid="segment-label-search-previous"
				/>
				<IconButton
					icon="chevronD"
					iconSize={13}
					size={22}
					disabled={matches.length === 0}
					onClick={onNext}
					title="次の一致"
					data-testid="segment-label-search-next"
				/>
			</div>

			{replacementOpen && (
				<>
					<div className="flex min-w-0 items-center gap-1">
						<div className="h-[22px] w-[22px] shrink-0" />
						<label className={SEARCH_FRAME_CLASS}>
							<input
								type="text"
								value={replacement}
								onChange={(event) => onReplacementChange(event.target.value)}
								onKeyDown={handleReplacementKeyDown}
								placeholder="置換後のラベル"
								className={SEARCH_FIELD_CLASS}
								data-testid="segment-label-replacement-input"
							/>
						</label>
					</div>
					<div className="flex items-center justify-end gap-1">
						<ConfirmButton
							label="置換"
							loading={false}
							size="sm"
							variant="secondary"
							disabled={matches.length === 0}
							onClick={onReplace}
							data-testid="segment-label-replace"
						/>
						<ConfirmButton
							label="すべて"
							loading={false}
							size="sm"
							variant="secondary"
							disabled={matches.length === 0}
							onClick={onReplaceAll}
							data-testid="segment-label-replace-all"
						/>
					</div>
				</>
			)}
		</div>
	);
}
